import React, { useMemo } from 'react';
import { DatePicker, parseDate } from '@ark-ui/react/date-picker';
import { Portal } from '../../../components/Portal';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface DatePickerInputProps extends BaseInputProps<string> {
  /** Minimální volitelné datum (ISO `YYYY-MM-DD`). */
  min?: string;
  /** Maximální volitelné datum (ISO `YYYY-MM-DD`). */
  max?: string;
  /** BCP-47 locale pro zobrazení data v inputu (RHF drží vždy ISO). */
  locale?: string;
  /** Lokalizovaný popisek tlačítka kalendáře. */
  triggerLabel?: string;
  className?: string;
}

const POPOVER_POSITIONING = { placement: 'bottom-start', gutter: 4 } as const;

const toDateValue = (iso: string | undefined | null) => {
  if (!iso) return undefined;
  try {
    return parseDate(iso);
  } catch {
    return undefined;
  }
};

/**
 * Atomický výběr data postavený na Ark UI `DatePicker` (denní pohled).
 *
 * RHF drží hodnotu jako ISO řetězec `YYYY-MM-DD` — bezpečně serializovatelný
 * a přímo kompatibilní se Zod validátory pracujícími nad stringem (např.
 * `futureDateSchema`). `ids.input` mapuje `id` z `<FormField />`.
 */
export const DatePickerInput: React.FC<DatePickerInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  labelId,
  testAttributes,
  min,
  max,
  locale = 'cs-CZ',
  triggerLabel = 'Otevřít kalendář',
  className,
}) => {
  const dateValue = useMemo(() => {
    const parsed = toDateValue(value);
    return parsed ? [parsed] : [];
  }, [value]);

  const ids = useMemo(
    () => ({ input: () => id, label: () => labelId }),
    [id, labelId]
  );

  return (
    <DatePicker.Root
      name={name}
      ids={ids}
      value={dateValue}
      min={toDateValue(min)}
      max={toDateValue(max)}
      locale={locale}
      disabled={disabled}
      readOnly={readOnly}
      onValueChange={(details) => {
        // `details.valueAsString` je lokalizovaný formát (pro zobrazení);
        // RHF drží kanonické ISO z DateValue.toString().
        onChange(details.value[0]?.toString() ?? '');
        onBlur();
      }}
      positioning={POPOVER_POSITIONING}
      lazyMount
      unmountOnExit
      className={clsx(styles.colorRoot, className)}
    >
      <DatePicker.Control className={styles.dateControl}>
        <DatePicker.Input
          ref={ref}
          placeholder={placeholder}
          onBlur={onBlur}
          className={styles.dateInput}
          aria-describedby={describedBy}
          aria-invalid={hasError || undefined}
          {...testAttributes}
        />
        <DatePicker.Trigger
          className={styles.dateTrigger}
          aria-label={triggerLabel}
        >
          <Icon name="calendar" size={18} />
        </DatePicker.Trigger>
      </DatePicker.Control>
      <Portal>
        <DatePicker.Positioner>
          <DatePicker.Content
            className={styles.dateContent}
            data-testid={`${testAttributes['data-testid']}-calendar`}
          >
            <DatePicker.View view="day">
              <DatePicker.Context>
                {(api) => (
                  <>
                    <DatePicker.ViewControl className={styles.dateViewControl}>
                      <DatePicker.PrevTrigger className={styles.datePrevTrigger}>
                        <Icon name="chevron-left" size={16} />
                      </DatePicker.PrevTrigger>
                      <DatePicker.RangeText className={styles.dateViewTrigger} />
                      <DatePicker.NextTrigger className={styles.dateNextTrigger}>
                        <Icon name="chevron-right" size={16} />
                      </DatePicker.NextTrigger>
                    </DatePicker.ViewControl>
                    <DatePicker.Table className={styles.dateTable}>
                      <DatePicker.TableHead>
                        <DatePicker.TableRow>
                          {api.weekDays.map((weekDay, index) => (
                            <DatePicker.TableHeader
                              key={index}
                              className={styles.dateTableHeader}
                            >
                              {weekDay.narrow}
                            </DatePicker.TableHeader>
                          ))}
                        </DatePicker.TableRow>
                      </DatePicker.TableHead>
                      <DatePicker.TableBody>
                        {api.weeks.map((week, weekIndex) => (
                          <DatePicker.TableRow key={weekIndex}>
                            {week.map((day, dayIndex) => (
                              <DatePicker.TableCell key={dayIndex} value={day}>
                                <DatePicker.TableCellTrigger
                                  className={styles.dateCellTrigger}
                                >
                                  {day.day}
                                </DatePicker.TableCellTrigger>
                              </DatePicker.TableCell>
                            ))}
                          </DatePicker.TableRow>
                        ))}
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>
          </DatePicker.Content>
        </DatePicker.Positioner>
      </Portal>
    </DatePicker.Root>
  );
};

DatePickerInput.displayName = 'DatePickerInput';
